"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import WaveDropTime from "./time/WaveDropTime";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { ArtistPreviewModal } from "./ArtistPreviewModal";
import { ArtistActivityBadge } from "./ArtistActivityBadge";
import { WaveCreatorBadge } from "./WaveCreatorBadge";
import { WaveCreatorPreviewModal } from "./WaveCreatorPreviewModal";
import { useMemo, useCallback } from "react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useArtistPreviewModal } from "@/hooks/useArtistPreviewModal";
import { useWaveCreatorPreviewModal } from "@/hooks/useWaveCreatorPreviewModal";
import { useCompactMode } from "@/contexts/CompactModeContext";
interface WaveDropHeaderProps {
  readonly drop: ApiDrop;
  readonly isStorm: boolean;
  readonly currentPartIndex: number;
  readonly partsCount: number;
  readonly showWaveInfo: boolean;
  readonly badge?: React.ReactNode | undefined;
  readonly showActionsButton?: boolean | undefined;
  readonly onOpenActions?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
}
const WaveDropHeader: React.FC<WaveDropHeaderProps> = t0 => {
  const $ = _c(73);
  const {
    drop,
    isStorm,
    currentPartIndex,
    partsCount,
    showWaveInfo,
    badge,
    showActionsButton: t1,
    onOpenActions
  } = t0;
  const showActionsButton = t1 === undefined ? false : t1;
  const router = useRouter();
  const compact = useCompactMode();
  const {
    isModalOpen: isArtistPreviewOpen,
    modalInitialTab,
    handleBadgeClick: handleArtistBadgeClick,
    handleModalClose: handleArtistModalClose
  } = useArtistPreviewModal();
  const {
    isModalOpen: isWaveCreatorPreviewOpen,
    handleBadgeClick: handleWaveCreatorBadgeClick,
    handleModalClose: handleWaveCreatorModalClose
  } = useWaveCreatorPreviewModal();
  const submissionCount = drop.author.active_main_stage_submission_ids.length || 0;
  const hasSubmissions = submissionCount > 0;
  const winnerCount = drop.author.winner_main_stage_drop_ids.length || 0;
  const isWinner = winnerCount > 0;
  const hasActivityBadge = hasSubmissions || isWinner;
  const isWaveCreator = drop.author.is_wave_creator;
  let t2;
  if ($[0] !== router) {
    t2 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[0] = router;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleNavigation = t2;
  const t3 = `tw-mb-0 tw-font-semibold tw-leading-none ${compact ? "tw-text-sm" : "tw-text-md"}`;
  const t4 = drop.author.handle ?? drop.author.id;
  let t5;
  if ($[2] !== drop.author.handle || $[3] !== drop.author.primary_address || $[4] !== handleNavigation) {
    t5 = e_0 => handleNavigation(e_0, `/${drop.author.handle ?? drop.author.primary_address}`);
    $[2] = drop.author.handle;
    $[3] = drop.author.primary_address;
    $[4] = handleNavigation;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = `/${drop.author.handle ?? drop.author.primary_address}`;
  let t7;
  if ($[6] !== drop.author.handle || $[7] !== t5 || $[8] !== t6) {
    t7 = <Link onClick={t5} href={t6} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-opacity-80 desktop-hover:hover:tw-underline">{drop.author.handle}</Link>;
    $[6] = drop.author.handle;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t4 || $[11] !== t7) {
    t8 = <UserProfileTooltipWrapper user={t4}>{t7}</UserProfileTooltipWrapper>;
    $[10] = t4;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t3 || $[14] !== t8) {
    t9 = <p className={t3}>{t8}</p>;
    $[13] = t3;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== drop.author.level) {
    t10 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[16] = drop.author.level;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== drop.id || $[19] !== handleArtistBadgeClick || $[20] !== hasActivityBadge || $[21] !== submissionCount || $[22] !== winnerCount) {
    t11 = hasActivityBadge && <ArtistActivityBadge submissionCount={submissionCount} winCount={winnerCount} onBadgeClick={handleArtistBadgeClick} tooltipId={`header-activity-badge-${drop.id}`} />;
    $[18] = drop.id;
    $[19] = handleArtistBadgeClick;
    $[20] = hasActivityBadge;
    $[21] = submissionCount;
    $[22] = winnerCount;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== drop.id || $[25] !== handleWaveCreatorBadgeClick || $[26] !== isWaveCreator) {
    t12 = isWaveCreator && <WaveCreatorBadge tooltipId={`wave-creator-${drop.id}`} onBadgeClick={handleWaveCreatorBadgeClick} />;
    $[24] = drop.id;
    $[25] = handleWaveCreatorBadgeClick;
    $[26] = isWaveCreator;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" />;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== drop.created_at) {
    t14 = <WaveDropTime timestamp={drop.created_at} />;
    $[29] = drop.created_at;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t14 || $[35] !== t9) {
    t15 = <div className="tw-flex tw-items-center tw-gap-x-2">{t9}{t10}{t11}{t12}{t13}{t14}</div>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t14;
    $[35] = t9;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== badge) {
    t16 = !!badge && <div className="tw-ml-2">{badge}</div>;
    $[37] = badge;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t15 || $[40] !== t16) {
    t17 = <div className="tw-flex tw-items-center tw-gap-x-2">{t15}{t16}</div>;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== onOpenActions || $[43] !== showActionsButton) {
    t18 = showActionsButton && onOpenActions && <button type="button" aria-label="Open drop actions" onClick={onOpenActions} className="tw-hidden tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-p-1.5 tw-text-iron-300 active:tw-bg-iron-800 desktop-hover:hover:tw-bg-iron-900 desktop-hover:hover:tw-text-iron-100 md:tw-flex"><EllipsisVerticalIcon className="tw-h-5 tw-w-5" /></button>;
    $[42] = onOpenActions;
    $[43] = showActionsButton;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t17 || $[46] !== t18) {
    t19 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-2">{t17}{t18}</div>;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== drop.wave || $[49] !== handleNavigation || $[50] !== showWaveInfo) {
    t20 = showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage: waveMeta?.scope?.group?.is_direct_message ?? false,
        isApp: false
      });
      return <Link onClick={e_1 => handleNavigation(e_1, waveHref)} href={waveHref} className="tw-leading-0 -tw-mt-1 tw-mb-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[48] = drop.wave;
    $[49] = handleNavigation;
    $[50] = showWaveInfo;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t20) {
    t21 = <div>{t20}</div>;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== currentPartIndex || $[55] !== isStorm || $[56] !== partsCount) {
    t22 = isStorm && <div className="tw-relative tw-mt-2 tw-inline-flex"><span className="tw-mb-1.5 tw-text-xs tw-text-iron-50">{currentPartIndex + 1} /{" "}<span className="tw-text-iron-400">{partsCount}</span></span></div>;
    $[54] = currentPartIndex;
    $[55] = isStorm;
    $[56] = partsCount;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== drop.author || $[59] !== handleArtistModalClose || $[60] !== isArtistPreviewOpen || $[61] !== modalInitialTab) {
    t23 = <ArtistPreviewModal isOpen={isArtistPreviewOpen} onClose={handleArtistModalClose} user={drop.author} initialTab={modalInitialTab} />;
    $[58] = drop.author;
    $[59] = handleArtistModalClose;
    $[60] = isArtistPreviewOpen;
    $[61] = modalInitialTab;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== drop.author || $[64] !== handleWaveCreatorModalClose || $[65] !== isWaveCreatorPreviewOpen) {
    t24 = <WaveCreatorPreviewModal isOpen={isWaveCreatorPreviewOpen} onClose={handleWaveCreatorModalClose} user={drop.author} />;
    $[63] = drop.author;
    $[64] = handleWaveCreatorModalClose;
    $[65] = isWaveCreatorPreviewOpen;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  let t25;
  if ($[67] !== t19 || $[68] !== t21 || $[69] !== t22 || $[70] !== t23 || $[71] !== t24) {
    t25 = <>{t19}{t21}{t22}{t23}{t24}</>;
    $[67] = t19;
    $[68] = t21;
    $[69] = t22;
    $[70] = t23;
    $[71] = t24;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  return t25;
};
export default WaveDropHeader;